// Integrações nativas do app (Capacitor): botão voltar do Android, insets de
// safe-area (edge-to-edge) e contraste da status bar.
//
// Só roda dentro do app empacotado (window.__MAPIARE_NATIVE__). No navegador é
// no-op — os imports dos plugins são dinâmicos e nunca carregam na web.
// Inicializado uma vez por página pelo shell (shared/nav.js → wireMoreSheet).

// Páginas onde NÃO aplicamos padding-top no body: o mapa é imersivo (desenha
// atrás da status bar) e afasta a busca via .map-topbar (var --mp-safe-top).
const NO_BODY_TOP_PAD = ['mapa'];

function isMapPage() {
  return NO_BODY_TOP_PAD.some((p) => location.pathname.includes(p));
}

// Piso mínimo de safe-area no app (px). Garante que a topbar do mapa e os
// headers fiquem ABAIXO da status bar mesmo se a leitura nativa de inset vier
// 0/atrasada. Se o inset real for maior (notch/câmera), usamos o maior.
const MIN_SAFE_TOP = 34;
const MIN_SAFE_BOTTOM = 12;

function setInsetVars(top, bottom) {
  const e = document.documentElement.style;
  e.setProperty('--mp-safe-top', Math.max(top || 0, MIN_SAFE_TOP) + 'px');
  e.setProperty('--mp-safe-bottom', Math.max(bottom || 0, MIN_SAFE_BOTTOM) + 'px');
}

// Aplica o piso imediatamente e, se o MainActivity expôs os insets reais
// (window.MpNativeInsets), usa o maior valor. Roda a cada página (MPA).
// Retorna true quando já leu valores reais (>0) — para parar as re-tentativas.
function applyNativeInsets() {
  let top = 0, bottom = 0, got = false;
  try {
    const ni = window.MpNativeInsets;
    if (ni) {
      top = Number(ni.top()) || 0;
      bottom = Number(ni.bottom()) || 0;
      got = top > 0 || bottom > 0;
    }
  } catch { /* usa só o piso */ }
  setInsetVars(top, bottom);
  return got;
}

export async function initNativeApp() {
  if (typeof window === 'undefined' || !window.__MAPIARE_NATIVE__) return;
  if (window.__nativeAppInit) return;
  window.__nativeAppInit = true;

  // 1) Botão voltar do Android: navega no histórico (como no navegador) e só
  //    manda o app pro background quando não há pra onde voltar.
  try {
    const { App } = await import('@capacitor/app');
    App.addListener('backButton', (ev) => {
      const canGoBack = (ev && ev.canGoBack) || window.history.length > 1;
      console.log('[native] backButton canGoBack=' + canGoBack + ' len=' + window.history.length);
      if (canGoBack) window.history.back();
      else App.minimizeApp(); // background em vez de fechar o app
    });
  } catch (e) { console.warn('[native] backButton indisponível:', e?.message || e); }

  // 2) Safe-area (edge-to-edge). O Android 15+/targetSdk 36 FORÇA edge-to-edge:
  //    as barras ficam TRANSPARENTES e o conteúdo desenha atrás — e o
  //    env(safe-area-inset-*) do Capacitor vem ZERADO. O MainActivity lê os
  //    insets nativos e injeta --mp-safe-top / --mp-safe-bottom; aqui
  //    garantimos que a var esteja setada nesta página e aplicamos o
  //    padding-top no body (exceto no mapa, que se afasta via .map-topbar).
  //    O fundo do app (--bg) aparece atrás da barra transparente → casa com o
  //    tema. O env() fica como fallback (web / iOS).
  if (!isMapPage() && !document.getElementById('safeAreaStyle')) {
    const s = document.createElement('style');
    s.id = 'safeAreaStyle';
    s.textContent =
      'body{box-sizing:border-box;'
      + 'padding-top:var(--mp-safe-top, env(safe-area-inset-top,0px));}';
    document.head.appendChild(s);
  }
  // Lê os insets já; se ainda vier 0, re-tenta (o listener de insets nativo
  // pode disparar depois do primeiro paint).
  if (!applyNativeInsets()) {
    let tries = 0;
    const iv = setInterval(() => {
      if (applyNativeInsets() || ++tries >= 8) clearInterval(iv);
    }, 120);
  }

  // 3) Contraste dos ícones da status bar conforme o tema. setStyle controla a
  //    aparência (clara/escura) dos ícones e funciona no edge-to-edge — só o
  //    setBackgroundColor é ignorado no Android 15+.
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    const apply = async () => {
      const dark = document.documentElement.getAttribute('data-theme') === 'dark';
      // Style.Dark = ícones claros (fundo escuro); Style.Light = ícones escuros
      // (fundo claro).
      try { await StatusBar.setStyle({ style: dark ? Style.Dark : Style.Light }); }
      catch { /* noop */ }
    };
    await apply();
    new MutationObserver(apply).observe(document.documentElement, {
      attributes: true, attributeFilter: ['data-theme'],
    });
  } catch (e) { console.warn('[native] status-bar indisponível:', e?.message || e); }
}
